'use client'

import { Badge } from '@/components/ui/badge'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { siteSections } from '@/lib/site-sections'
import {
  allUpdates,
  allLinks,
  allGuides,
  allAssistants,
  allBenchmarks,
  allModels,
  allCompanies,
  allPeople,
  allTools,
  allMcps,
} from 'content-collections'
import { Brain, ChevronLeft, Monitor, Moon, Palette, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'

const collections = [
  { base: 'updates', items: allUpdates },
  { base: 'links', items: allLinks },
  { base: 'guides', items: allGuides },
  { base: 'assistants', items: allAssistants },
  { base: 'benchmarks', items: allBenchmarks },
  { base: 'models', items: allModels },
  { base: 'companies', items: allCompanies },
  { base: 'people', items: allPeople },
  { base: 'tools', items: allTools },
  { base: 'mcp', items: allMcps },
]

export const CommandPalette = () => {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<'root' | 'color'>('root')
  const router = useRouter()
  const { theme, resolvedTheme, setTheme } = useTheme()

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  useEffect(() => {
    if (!open) setView('root')
  }, [open])

  useEffect(() => {
    const openPalette = () => setOpen(true)
    window.addEventListener('open-command-palette', openPalette)
    return () => window.removeEventListener('open-command-palette', openPalette)
  }, [])

  const pages = useMemo(
    () =>
      collections.flatMap(({ base, items }) =>
        (items as any[])
          .filter((p) => (p as any).published !== false)
          .map((p: any) => ({
            href: `/${base}/${p._meta.path}`,
            title: p.title,
            tag: (p as any).tags?.[0],
            creator: base === 'models' ? (p as any).creator?.name : undefined,
            section: base,
            year:
              'release_date' in p && p.release_date
                ? p.release_date.getFullYear()
                : p.date
                  ? p.date.getFullYear()
                  : 0,
          }))
      ),
    []
  )

  const runCommand = (href: string) => {
    setOpen(false)
    router.push(href)
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {view === 'root' ? (
          <>
            {/* 
            <CommandGroup heading="Actions">
              <CommandItem onSelect={() => setView('color')}>
                <Palette className="mr-2 h-4 w-4" />
                <span>Color mode…</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            */}
            <CommandGroup heading="Navigate">
              {siteSections.map((link) => (
                <CommandItem
                  key={link.href}
                  onSelect={() => runCommand(link.href)}
                >
                  <link.icon className="mr-2 h-4 w-4" />
                  <span>{link.label}</span>
                </CommandItem>
              ))}
              <CommandItem onSelect={() => runCommand('/ai-research')}>
                <Brain className="mr-2 h-4 w-4" />
                <span>AI Research</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Pages">
              {pages.map((page) => {
                const section = siteSections.find(
                  (s) => s.href.slice(1) === page.section
                )
                const Icon = section?.icon
                const label = section?.label.toLowerCase() || page.section
                return (
                  <CommandItem
                    key={page.href}
                    onSelect={() => runCommand(page.href)}
                    keywords={[String(page.year), page.section, label]}
                  >
                    {Icon ? <Icon className="mr-2 h-4 w-4" /> : null}
                    <span>{page.title}</span>
                    {page.tag ? (
                      <Badge variant="outline" className="ml-auto">
                        {page.tag}
                      </Badge>
                    ) : page.creator ? (
                      <Badge variant="outline" className="ml-auto">
                        {page.creator}
                      </Badge>
                    ) : null}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </>
        ) : (
          <>
            <CommandGroup heading="Color">
              <CommandItem onSelect={() => setView('root')}>
                <ChevronLeft className="mr-2 h-4 w-4" />
                <span>Back</span>
              </CommandItem>
              <CommandItem
                onSelect={() =>
                  setTheme(
                    theme === 'system'
                      ? resolvedTheme === 'dark'
                        ? 'light'
                        : 'dark'
                      : theme === 'dark'
                        ? 'light'
                        : 'dark'
                  )
                }
              >
                <Palette className="mr-2 h-4 w-4" />
                <span>Toggle color</span>
              </CommandItem>
              <CommandItem onSelect={() => setTheme('system')}>
                <Monitor className="mr-2 h-4 w-4" />
                <span>Use system preferences</span>
              </CommandItem>
              <CommandItem onSelect={() => setTheme('dark')}>
                <Moon className="mr-2 h-4 w-4" />
                <span>Dark mode</span>
              </CommandItem>
              <CommandItem onSelect={() => setTheme('light')}>
                <Sun className="mr-2 h-4 w-4" />
                <span>Light mode</span>
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  )
}
